import React from "react";
import Select from "react-select";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Avatar } from "@mui/material";

import { initialsOf } from "../utils/commentUtils";
import { avatarColorOf } from "../utils/avatarUtils";

// Shared react-select wrapper styled to match the app's MUI fields (height 38,
// rounded corners, theme-aware colors) so pages don't each hand-roll styles.
// `options`: [{ value, label }]. `value`/`onChange` work with raw scalar
// values (or arrays of them when `isMulti`), not react-select's option objects.
// Pass `withAvatar` to prefix each option/value with an initials avatar (e.g.
// assignee/reporter pickers), matching the app's people-picker convention.
function AppSelect({
  label,
  icon: Icon,
  options = [],
  value,
  onChange,
  isMulti = false,
  isClearable = false,
  isDisabled = false,
  isLoading = false,
  placeholder = "Select...",
  minWidth,
  width,
  error = false,
  helperText,
  withAvatar = false,
  ...rest
}) {
  const theme = useTheme();

  const formatOptionLabel = withAvatar
    ? (option) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar sx={{ width: 20, height: 20, fontSize: 10, bgcolor: avatarColorOf(option.value) }}>
            {initialsOf(option.label)}
          </Avatar>
          <span>{option.label}</span>
        </Box>
      )
    : undefined;

  const selectedValue = isMulti
    ? options.filter((option) => Array.isArray(value) && value.includes(option.value))
    : options.find((option) => option.value === value) || null;

  const handleChange = (selected) => {
    if (!onChange) {
      return;
    }

    if (isMulti) {
      onChange(Array.isArray(selected) ? selected.map((option) => option.value) : []);
      return;
    }

    onChange(selected ? selected.value : "");
  };

  const styles = {
    container: (base) => ({ ...base, minWidth, width }),
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      height: isMulti ? "auto" : 38,
      borderRadius: 8,
      background: theme.palette.background.paper,
      borderColor: error
        ? theme.palette.error.main
        : state.isFocused
        ? theme.palette.primary.main
        : theme.palette.divider,
      boxShadow: state.isFocused ? `0 0 0 1px ${error ? theme.palette.error.main : theme.palette.primary.main}` : "none",
      "&:hover": { borderColor: error ? theme.palette.error.main : theme.palette.primary.main },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px", flexWrap: "nowrap" }),
    input: (base) => ({ ...base, color: theme.palette.text.primary, margin: 0, padding: 0 }),
    singleValue: (base) => ({
      ...base,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    placeholder: (base) => ({
      ...base,
      color: theme.palette.text.secondary,
      fontSize: 14,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, color: theme.palette.text.secondary, padding: 6 }),
    clearIndicator: (base) => ({ ...base, color: theme.palette.text.secondary, padding: 6 }),
    menu: (base) => ({
      ...base,
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
      zIndex: 1300,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 1300 }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      background: state.isSelected
        ? theme.palette.primary.main
        : state.isFocused
        ? theme.palette.action.hover
        : "transparent",
      color: state.isSelected ? "#fff" : theme.palette.text.primary,
    }),
    multiValue: (base) => ({ ...base, background: theme.palette.action.selected }),
    multiValueLabel: (base) => ({ ...base, color: theme.palette.text.primary }),
  };

  return (
    <Box sx={{ minWidth, width }}>
      {label && (
        <Typography
          sx={{ display: "flex", alignItems: "center", gap: 0.6, fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}
        >
          {Icon && <Icon sx={{ fontSize: 16 }} />}
          {label}
        </Typography>
      )}

      <Select
        options={options}
        value={selectedValue}
        onChange={handleChange}
        isMulti={isMulti}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        placeholder={placeholder}
        menuPortalTarget={document.body}
        formatOptionLabel={formatOptionLabel}
        styles={styles}
        {...rest}
      />

      {helperText && (
        <Typography sx={{ fontSize: 12, color: error ? "error.main" : "text.secondary", mt: 0.7, minHeight: 20 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

export default AppSelect;
